import { List, Map } from 'immutable';

import { Utilities } from '../../../utilities/utilities';

export interface LayoutCalendarEvent {
  id: number;
  serviceID: number;
  title: string;
  content: string;
  startTime: Date;
  endTime: Date;
  service: any;
  booking: any;
  allDay: boolean;
  cssClass: string;
  part: number;
  laneIndex: number;
  laneCount: number;
  startOffsetMinutes: number;
  set(key: string, value: any): LayoutCalendarEvent;
}

interface ActiveLaneAssignment {
  event: LayoutCalendarEvent;
  laneIndex: number;
}

interface PositionedEvent {
  event: LayoutCalendarEvent;
  laneIndex: number;
  laneCount: number;
}

export function layoutDayEvents(
  eventsByTime: Map<string, List<LayoutCalendarEvent>>
): Map<string, List<LayoutCalendarEvent>> {
  const sortedEvents = flattenDayEvents(eventsByTime).sort(compareCalendarEvents);

  if (sortedEvents.length === 0) {
    return Map<string, List<LayoutCalendarEvent>>();
  }

  let activeAssignments: ActiveLaneAssignment[] = [];
  const positionedEvents = new globalThis.Map<string, PositionedEvent>();

  sortedEvents.forEach((event) => {
    const currentStart = event.startTime.getTime();

    // Exact-touch boundaries do not overlap, so free lanes before assignment.
    activeAssignments = activeAssignments.filter(
      ({ event: activeEvent }) => activeEvent.endTime.getTime() > currentStart
    );

    const laneIndex = getLowestAvailableLane(activeAssignments);
    const newAssignment: ActiveLaneAssignment = { event, laneIndex };
    activeAssignments = [...activeAssignments, newAssignment];

    const activeLaneCount = activeAssignments.length;

    activeAssignments.forEach((assignment) => {
      const eventKey = getEventKey(assignment.event);
      const positionedEvent = positionedEvents.get(eventKey);

      if (positionedEvent) {
        positionedEvents.set(eventKey, {
          ...positionedEvent,
          laneCount: Math.max(positionedEvent.laneCount, activeLaneCount),
        });
        return;
      }

      positionedEvents.set(eventKey, {
        event: assignment.event,
        laneIndex: assignment.laneIndex,
        laneCount: activeLaneCount,
      });
    });
  });

  return Array.from(positionedEvents.values()).reduce((segmentsByStartTime, positionedEvent) => {
    const eventTimeStamp = Utilities.formatDate(positionedEvent.event.startTime);
    const existingSegments =
      (segmentsByStartTime.get(eventTimeStamp) as List<LayoutCalendarEvent>) ||
      List<LayoutCalendarEvent>();

    return segmentsByStartTime.set(
      eventTimeStamp,
      existingSegments
        .push(
          positionedEvent.event
            .set('laneIndex', positionedEvent.laneIndex)
            .set('laneCount', positionedEvent.laneCount)
            .set('startOffsetMinutes', 0)
        )
        .sort((left, right) => left.laneIndex - right.laneIndex)
    );
  }, Map<string, List<LayoutCalendarEvent>>());
}

function flattenDayEvents(
  eventsByTime: Map<string, List<LayoutCalendarEvent>>
): LayoutCalendarEvent[] {
  if (!Map.isMap(eventsByTime)) {
    return [];
  }

  const flattenedEvents: LayoutCalendarEvent[] = [];

  eventsByTime.forEach((value) => {
    const events = value as List<LayoutCalendarEvent>;

    if (!List.isList(events)) {
      return;
    }

    events.forEach((eventValue) => {
      const event = eventValue as LayoutCalendarEvent;

      if (
        event &&
        event.startTime instanceof Date &&
        event.endTime instanceof Date &&
        event.endTime.getTime() > event.startTime.getTime()
      ) {
        flattenedEvents.push(event);
      }
    });
  });

  return flattenedEvents;
}

function getLowestAvailableLane(activeAssignments: ActiveLaneAssignment[]): number {
  const occupiedLanes = new Set(activeAssignments.map(({ laneIndex }) => laneIndex));
  let laneIndex = 0;

  while (occupiedLanes.has(laneIndex)) {
    laneIndex++;
  }

  return laneIndex;
}

function compareCalendarEvents(left: LayoutCalendarEvent, right: LayoutCalendarEvent): number {
  const startDiff = left.startTime.getTime() - right.startTime.getTime();

  if (startDiff !== 0) {
    return startDiff;
  }

  const endDiff = left.endTime.getTime() - right.endTime.getTime();

  if (endDiff !== 0) {
    return endDiff;
  }

  const serviceDiff = (left.serviceID || 0) - (right.serviceID || 0);

  if (serviceDiff !== 0) {
    return serviceDiff;
  }

  return (left.id || 0) - (right.id || 0);
}

function getEventKey(event: LayoutCalendarEvent): string {
  return [
    event.id,
    event.serviceID,
    event.part,
    event.startTime.getTime(),
    event.endTime.getTime(),
  ].join(':');
}
